'use client';

import { SEARCH_TYPE } from '@/constants/searchType';
import { useState, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import { useSavedFilters } from '@/hooks/publicSearch/useSavedFilters';
import { useSnackbar } from 'notistack';
import {
  savedFiltersAreEquivalent,
  supportsSavedFilters,
} from '@/publicSearch/store/useSavedFiltersStore';
import { useCatalogueFilters } from '@/hooks/publicSearch/useCatalogueFilters';
import { LogicalOperator } from '@/components/publicSearch/constants';
import { SNACKBAR_VARIANT } from '@/constants/notifications';
import { SALE_DATE_FILTER } from '@/constants/saleFilters';

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  borderRadius: '4px',
  border: '1px solid',
  borderColor: theme.palette.primary.main,
  background: 'background.paper',
}));

interface SaveFilterButtonProps {
  disabled?: boolean;
  /** When set, render only the popover (no trigger button). Use with open/onClose for controlled mode (e.g. from a menu). */
  anchorEl?: HTMLElement | null;
  /** Controlled open state when using anchorEl. */
  open?: boolean;
  /** Called when popover should close (when using controlled mode). */
  onClose?: () => void;
}

export default function SaveFilterButton({
  disabled,
  anchorEl: anchorElProp,
  open: openProp,
  onClose,
}: SaveFilterButtonProps) {
  const [internalAnchor, setInternalAnchor] =
    useState<HTMLButtonElement | null>(null);
  const [filterName, setFilterName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const searchType = useSearchType();
  const { filters, filterGroups, groupsOperator } = useFilterStore(searchType);
  const gridSortModel = useGridStore((s) => s.gridSortModel);
  const { basicSearchInput } = useSearchStore();
  const { saleDateFilter, saleTypeFilterArray } = useCatalogueFilters();
  const { saveFilter, savedFilters, isLoading } = useSavedFilters();
  const { enqueueSnackbar } = useSnackbar();

  const isControlled = anchorElProp !== undefined && openProp !== undefined;
  const anchorEl = isControlled ? anchorElProp : internalAnchor;
  const open = isControlled ? openProp : Boolean(internalAnchor);

  const groupsOpString =
    groupsOperator === LogicalOperator.or
      ? LogicalOperator.or
      : LogicalOperator.and;

  const hasGroupedFilters = filterGroups.some((g) => g.filters.length > 0);

  const currentFilterDraft = useMemo(
    () => ({
      searchType,
      filters,
      searchTerm: hasGroupedFilters ? '' : basicSearchInput,
      saleDateFilter:
        searchType === SEARCH_TYPE.SALE_CATALOGUES ? saleDateFilter : null,
      saleTypeFilterValues:
        searchType === SEARCH_TYPE.SALE_CATALOGUES ? saleTypeFilterArray : [],
      gridSortModel,
      isAiMode: false,
      filterGroups,
      groupsOperator: groupsOpString,
      aiSearchPrompt: '',
    }),
    [
      basicSearchInput,
      filterGroups,
      filters,
      gridSortModel,
      groupsOpString,
      hasGroupedFilters,
      saleDateFilter,
      saleTypeFilterArray,
      searchType,
    ],
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setInternalAnchor(event.currentTarget);
    setFilterName('');
    // Focus input after popover opens
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleClose = () => {
    if (!isControlled) setInternalAnchor(null);
    setFilterName('');
    onClose?.();
  };

  const handleSave = async () => {
    const name = filterName.trim() || `Saved Filter ${savedFilters.length + 1}`;
    try {
      await saveFilter({
        ...currentFilterDraft,
        name,
      });
      enqueueSnackbar(`Search "${name}" saved`, {
        variant: SNACKBAR_VARIANT.SUCCESS,
        autoHideDuration: 2000,
      });
      handleClose();
    } catch (error) {
      enqueueSnackbar('Failed to save search', {
        variant: SNACKBAR_VARIANT.ERROR,
        autoHideDuration: 3000,
      });
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      await handleSave();
    } else if (e.key === 'Escape') {
      handleClose();
    }
  };

  const hasActiveFilters =
    filters.length > 0 ||
    (basicSearchInput && basicSearchInput.trim() !== '') ||
    hasGroupedFilters ||
    gridSortModel.length > 0 ||
    (searchType === SEARCH_TYPE.SALE_CATALOGUES &&
      (saleDateFilter !== SALE_DATE_FILTER.CURRENT ||
        saleTypeFilterArray.length > 0));

  // Check if current filters match any saved filter
  const matchesExistingSavedFilter = useMemo(() => {
    return savedFilters.some((saved) =>
      savedFiltersAreEquivalent(saved, currentFilterDraft),
    );
  }, [currentFilterDraft, savedFilters]);

  // Only highlight if there are active filters AND they don't match a saved filter
  const shouldHighlight = hasActiveFilters && !matchesExistingSavedFilter;

  if (!supportsSavedFilters(searchType)) {
    return null;
  }

  return (
    <>
      {!isControlled && (
        <Tooltip
          title={
            matchesExistingSavedFilter ? 'Search already saved' : 'Save search'
          }
          placement="bottom"
        >
          <span>
            <StyledIconButton
              sx={{
                display: { xs: 'none', md: 'flex' },
                height: { xs: 32, md: 36 },
                width: { xs: 32, md: 36 },
              }}
              onClick={handleClick}
              disabled={disabled || !hasActiveFilters || isLoading}
              aria-label="Save search"
            >
              <BookmarkAddOutlinedIcon
                sx={{ fontSize: 18 }}
                color={shouldHighlight && !disabled ? 'primary' : 'action'}
              />
            </StyledIconButton>
          </span>
        </Tooltip>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              mt: 1,
              minWidth: 280,
            },
          },
        }}
      >
        <Typography variant="titlemedium" sx={{ mb: 1.5 }}>
          Save current search
        </Typography>
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          placeholder="Enter search name (optional)"
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => void handleSave()}
            disabled={isLoading}
          >
            Save
          </Button>
        </Box>
      </Popover>
    </>
  );
}
